/**
 * @fileoverview Day View Component for Room-Centric Planner
 * @module features/coordinator/planner/DayView
 * @description Timeline view showing all rooms and session times for a single day
 * @created 10-10-2025 20:56:00
 * @author MCHD Room Scheduler Team
 */

import React from 'react';
import {
  Paper,
  Text,
  Group,
  Stack,
  Badge,
  ScrollArea,
  Card,
  Tooltip,
  ActionIcon,
  Loader,
  Center,
  Table,
} from '@mantine/core';
import {
  IconMapPin,
  IconClock,
  IconUser,
  IconPlus,
  IconAlertTriangle,
  IconBuilding,
} from '@tabler/icons-react';
import { formatCalendarHeader, isDateToday, isDatePast, groupRoomsByFloor, enrichCellStatusAssignment } from './utils/calendarHelpers';

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface SessionTemplate {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
}

export interface Room {
  id: string;
  number: string;
  name: string;
  capacity: number;
  floorId: string;
  floor: {
    id: string;
    name: string;
    level: number;
    building: {
      id: string;
      name: string;
    };
  };
}

export interface Assignment {
  id: string;
  title: string;
  roomId: string;
  userId: string;
  sessionTemplateId: string;
  date: string;
  status: 'CONFIRMED' | 'CANCELLED';
  published: boolean;
  conflictCode?: string;
  // FIXED: 15-10-2025 19:30:00 - Add nested data from backend API
  user?: {
    id: string;
    name: string;
    role: string;
  };
  room?: {
    id: string;
    number: string;
    name: string;
  };
  sessionTime?: {
    id: string;
    name: string;
    startTime: string;
    endTime: string;
  };
  // Legacy support
  users?: {
    id: string;
    name: string;
  };
}

export interface CellStatus {
  roomId: string;
  sessionTemplateId: string;
  date: string;
  status: 'empty' | 'occupied' | 'blocked' | 'inactive' | 'conflict' | 'holiday';
  assignment?: Assignment;
  conflicts?: string[];
  holiday?: any;
}

export interface DayViewProps {
  /** Current viewing date */
  currentDate: Date;
  
  /** Rooms to display */
  rooms: Room[];
  
  /** Session templates/times */
  sessionTemplates: SessionTemplate[];
  
  /** Cell statuses data from API */
  cellStatuses: CellStatus[];
  
  /** Full assignments array with nested data */
  assignments?: Assignment[];
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Group by floor */
  groupByFloor?: boolean;
  
  /** Callback when cell is clicked for assignment creation */
  onCellClick?: ((roomId: string, sessionId: string, date: Date) => void) | undefined;
  
  /** Callback when assignment is clicked */
  onAssignmentClick?: ((assignment: Assignment) => void) | undefined;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get cell status for specific room and session
 * ENHANCED: 15-10-2025 17:50:00 - Extract assignment data from cellStatuses
 */
function getCellStatus(
  cellStatuses: CellStatus[],
  roomId: string,
  sessionId: string,
  date: Date
): CellStatus | undefined {
  const dateString = date.toISOString().split('T')[0];
  const cellStatus = cellStatuses.find(
    (cs) =>
      cs.roomId === roomId &&
      cs.sessionTemplateId === sessionId &&
      cs.date === dateString
  );
  
  // If cellStatus exists but doesn't have assignment data, try to find it
  if (cellStatus && cellStatus.status === 'occupied' && !cellStatus.assignment) {
    // Look for assignment data in the cellStatuses array
    // This handles cases where assignment data is stored separately
    const assignmentData = cellStatuses.find(
      (cs) => 
        cs.roomId === roomId && 
        cs.sessionTemplateId === sessionId && 
        cs.date === dateString &&
        cs.assignment
    );
    
    if (assignmentData?.assignment) {
      return {
        ...cellStatus,
        assignment: assignmentData.assignment
      };
    }
  }
  
  return cellStatus;
}

/**
 * Get display info for cell based on status
 */
function getCellDisplayInfo(cellStatus?: CellStatus) {
  if (!cellStatus) {
    return { text: 'Available', color: 'gray', bgColor: '#f8f9fa', icon: '' };
  }

  switch (cellStatus.status) {
    case 'occupied':
      // Check assignment status for proper display
      if (cellStatus.assignment) {
        switch (cellStatus.assignment.status) {
          case 'CONFIRMED':
            return {
              text: 'Assigned',
              color: 'green',
              bgColor: '#d1fae5',
              icon: '✓',
            };
          case 'CANCELLED':
            return {
              text: 'Cancelled',
              color: 'gray',
              bgColor: '#f3f4f6',
              icon: '✕',
            };
          default:
            return {
              text: 'Assigned',
              color: 'green',
              bgColor: '#d1fae5',
              icon: '✓',
            };
        }
      }
      // Fallback if no assignment data
      return {
        text: 'Assigned',
        color: 'green',
        bgColor: '#d1fae5',
        icon: '✓',
      };
    case 'blocked':
      return {
        text: 'Blocked',
        color: 'red',
        bgColor: '#fee2e2',
        icon: '🚫',
      };
    case 'conflict':
      return {
        text: 'Conflict',
        color: 'orange',
        bgColor: '#fff4e6',
        icon: '⚠️',
      };
    case 'holiday':
      return {
        text: 'Holiday',
        color: 'purple',
        bgColor: '#faf5ff',
        icon: '🎉',
      };
    case 'inactive':
      return {
        text: 'Inactive',
        color: 'gray',
        bgColor: '#f3f4f6',
        icon: '',
      };
    case 'empty':
    default:
      return {
        text: 'Available',
        color: 'green',
        bgColor: '#f0f9ff',
        icon: '',
      };
  }
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Cell Component
 */
interface CellComponentProps {
  room: Room;
  session: SessionTemplate;
  cellStatus?: CellStatus | undefined;
  date: Date;
  onCellClick?: (() => void) | undefined;
  onAssignmentClick?: (() => void) | undefined;
  isPast: boolean;
}

const CellComponent: React.FC<CellComponentProps> = ({
  cellStatus,
  onCellClick,
  onAssignmentClick,
  isPast,
}) => {
  const displayInfo = getCellDisplayInfo(cellStatus);
  const hasAssignment = cellStatus?.assignment;
  const hasConflict = !!cellStatus?.assignment?.conflictCode;

  return (
    <Paper
      p="xs"
      radius="sm"
      withBorder
      style={{
        minHeight: 60,
        backgroundColor: isPast ? '#f3f4f6' : displayInfo.bgColor,
        cursor: !isPast && (hasAssignment || onCellClick) ? 'pointer' : 'default',
        opacity: isPast ? 0.6 : 1,
        borderColor: hasConflict ? '#f59e0b' : undefined,
      }}
      onClick={() => {
        if (isPast) return;
        if (hasAssignment && onAssignmentClick) {
          onAssignmentClick();
        } else if (onCellClick) {
          onCellClick();
        }
      }}
    >
      {hasAssignment ? (
        (() => {
          // FIXED: 15-10-2025 19:30:00 - Use correct nested properties from backend API
          // Backend returns: user, room, sessionTime (not users, rooms, session_times)
          const roomData = cellStatus!.assignment!.room;
          const sessionData = cellStatus!.assignment!.sessionTime;
          const doctorData = cellStatus!.assignment!.user || cellStatus!.assignment!.users; // Support both new and legacy

          return (
            <Tooltip
              label={
                <Stack gap={4}>
                  <Text size="xs" fw={600}>{cellStatus!.assignment!.title || 'Untitled Assignment'}</Text>
                  <Text size="xs">Doctor: {doctorData?.name || 'Unknown Doctor'}</Text>
                  <Text size="xs">Room: {roomData?.number || 'Unknown'} - {roomData?.name || 'Unknown Room'}</Text>
                  <Text size="xs">Session: {sessionData?.name || 'Unknown Session'} ({sessionData?.startTime || 'Unknown'} - {sessionData?.endTime || 'Unknown'})</Text>
                  <Text size="xs">Status: {cellStatus!.assignment!.status}</Text>
                  <Text size="xs">Date: {new Date(cellStatus!.assignment!.date).toLocaleDateString()}</Text>
                  {cellStatus!.assignment!.published === false && <Text size="xs" c="orange">Unpublished</Text>}
                  {hasConflict && <Text size="xs" c="red">Conflict: {cellStatus!.assignment!.conflictCode}</Text>}
                </Stack>
              }
              multiline
              withArrow
              position="top"
              openDelay={300}
            >
          <Stack gap={4}>
            <Group gap="xs" justify="space-between" wrap="nowrap">
              <Text size="xs" fw={600} truncate style={{ flex: 1 }}>
                {cellStatus!.assignment!.title || 'Untitled'}
              </Text>
              {hasConflict && (
                <IconAlertTriangle size={14} color="#f59e0b" />
              )}
            </Group>
            
            {cellStatus!.assignment!.users && (
              <Group gap={4}>
                <IconUser size={12} color="gray" />
                <Text size="xs" c="dimmed" truncate>
                  {cellStatus!.assignment!.users.name}
                </Text>
              </Group>
            )}
            
            <Group gap={4}>
              <Badge size="xs" color={displayInfo.color} variant="light">
                {displayInfo.text}
              </Badge>
              {cellStatus!.assignment!.published === false && (
                <Badge size="xs" color="orange" variant="light">
                  Unpublished
                </Badge>
              )}
            </Group>
          </Stack>
        </Tooltip>
          );
        })()
      ) : (
        <Group gap="xs" justify="center" align="center" style={{ minHeight: 50 }}>
          <Text size="xs" c="dimmed">
            {displayInfo.icon} {displayInfo.text}
          </Text>
          {!isPast && cellStatus?.status === 'empty' && onCellClick && (
            <ActionIcon
              size="xs"
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                onCellClick();
              }}
            >
              <IconPlus size={12} />
            </ActionIcon>
          )}
        </Group>
      )}
    </Paper>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Day View Component
 * Displays detailed timeline for a single day showing all rooms and session times
 * 
 * @created 10-10-2025 20:56:00
 */
export const DayView: React.FC<DayViewProps> = ({
  currentDate,
  rooms,
  sessionTemplates,
  cellStatuses,
  assignments = [],
  isLoading = false,
  groupByFloor = true,
  onCellClick,
  onAssignmentClick,
}) => {
  const isToday = isDateToday(currentDate);
  const isPastDate = isDatePast(currentDate);

  // ENHANCED: 15-10-2025 17:50:00 - Debug data availability
  console.log('📅 [DayView] Data Debug:', {
    currentDate: currentDate.toISOString().split('T')[0],
    roomsCount: rooms.length,
    sessionTemplatesCount: sessionTemplates.length,
    cellStatusesCount: cellStatuses.length,
    cellStatusesWithAssignments: cellStatuses.filter(cs => cs.assignment).length,
    sampleCellStatus: cellStatuses[0]
  });

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (rooms.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Text ta="center" c="dimmed">
          No rooms available for this date
        </Text>
      </Paper>
    );
  }

  // Group rooms by floor if requested
  const roomGroups = groupByFloor ? groupRoomsByFloor(rooms as any) : { all: rooms };

  return (
    <Stack gap="md">
      {/* Date header */}
      <Paper p="md" withBorder bg={isToday ? 'blue.0' : 'white'}>
        <Group justify="space-between">
          <div>
            <Text size="xl" fw={700}>
              {formatCalendarHeader(currentDate, 'day')}
            </Text>
            {isToday && (
              <Badge color="blue" variant="filled" mt="xs">
                Today
              </Badge>
            )}
          </div>
          {sessionTemplates.length > 0 && (
            <Text size="sm" c="dimmed">
              {sessionTemplates.length} Session Times
            </Text>
          )}
        </Group>
      </Paper>

      {/* Timeline grid */}
      <ScrollArea h={600} type="auto">
        <Stack gap="lg">
          {Object.entries(roomGroups).map(([floorId, floorRooms]) => {
            const floor = floorRooms[0]?.floor;
            
            return (
              <Card key={floorId} p="md" withBorder>
                {groupByFloor && floor && (
                  <Group gap="xs" mb="md">
                    <IconBuilding size={18} />
                    <Text fw={600}>
                      {floor.building.name} - Floor {floor.level} ({floor.name})
                    </Text>
                    <Badge size="sm" variant="light">
                      {floorRooms.length} rooms
                    </Badge>
                  </Group>
                )}

                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ minWidth: 200 }}>
                        <Group gap="xs">
                          <IconMapPin size={16} />
                          <Text>Room</Text>
                        </Group>
                      </Table.Th>
                      {sessionTemplates.map((session) => (
                        <Table.Th key={session.id} style={{ minWidth: 180 }}>
                          <Stack gap={4}>
                            <Group gap="xs">
                              <IconClock size={14} />
                              <Text size="sm" fw={600}>
                                {session.name}
                              </Text>
                            </Group>
                            <Text size="xs" c="dimmed">
                              {session.startTime} - {session.endTime}
                            </Text>
                          </Stack>
                        </Table.Th>
                      ))}
                    </Table.Tr>
                  </Table.Thead>
                  
                  <Table.Tbody>
                    {floorRooms.map((room) => (
                      <Table.Tr key={room.id}>
                        <Table.Td>
                          <Stack gap={4}>
                            <Text size="sm" fw={600}>
                              {room.number}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                              {room.name}
                            </Text>
                            <Badge size="xs" variant="light">
                              Cap: {room.capacity}
                            </Badge>
                          </Stack>
                        </Table.Td>
                        
                        {sessionTemplates.map((session) => {
                          const cellStatus = getCellStatus(
                            cellStatuses,
                            room.id,
                            session.id,
                            currentDate
                          );
                          const enrichedCellStatus = cellStatus 
                            ? enrichCellStatusAssignment(cellStatus, assignments)
                            : undefined;

                          return (
                            <Table.Td key={session.id}>
                              <CellComponent
                                room={room}
                                session={session}
                                cellStatus={enrichedCellStatus}
                                date={currentDate}
                                isPast={isPastDate}
                                onCellClick={
                                  onCellClick
                                    ? () => onCellClick(room.id, session.id, currentDate)
                                    : undefined
                                }
                                onAssignmentClick={
                                  cellStatus?.assignment && onAssignmentClick
                                    ? () => onAssignmentClick(cellStatus.assignment!)
                                    : undefined
                                }
                              />
                            </Table.Td>
                          );
                        })}
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Card>
            );
          })}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};

export default DayView;

